import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { SelectInput } from './content-fields'
import type { ResolvedItemView, Translate } from './content-types'
import { MarkdownPreviewView } from './markdown-preview.view'

/**
 * Props for {@link ResolvePreviewView}.
 *
 * @property result - The resolved blocks, or null before the first preview.
 * @property missing - The server's `CODE (locale)` list on `TEXT_NOT_AVAILABLE`.
 * @property error - Any other translated failure.
 */
export interface ResolvePreviewViewProps {
	t: Translate
	locale: string
	locales: string[]
	result: { withProduct: ResolvedItemView[]; pageEnd: ResolvedItemView[] } | null
	missing: string | null
	error: string | null
	isBusy: boolean
	onLocaleChange: (locale: string) => void
	onPreview: () => void
}

function Block({ t, title, items }: { t: Translate; title: string; items: ResolvedItemView[] }) {
	return (
		<section className="flex flex-col gap-2 rounded-ds-md border border-ds-border p-3" aria-label={title}>
			<h3 className="text-ds-base font-semibold text-ds-text">{title}</h3>
			{items.length === 0 ? <p className="text-ds-sm text-ds-text-muted">{t('preview.none')}</p> : null}
			{items.map((item) => (
				<article key={item.id} className="flex flex-col gap-1" data-testid={`resolved-${item.code}`}>
					<h4 className="font-medium text-ds-text">{item.title}</h4>
					<MarkdownPreviewView label={item.code} markdown={item.body} />
					<span className="text-ds-xs text-ds-text-muted">{`v${item.versionNumber} · ${item.locale}`}</span>
				</article>
			))}
		</section>
	)
}

/** Preview of what an offer of the product renders, from `POST /content/resolve` (organism). */
export function ResolvePreviewView(props: ResolvePreviewViewProps): ReactElement {
	const { t } = props
	return (
		<section className="flex flex-col gap-3" aria-label={t('preview.title')}>
			<h2 className="text-ds-lg font-semibold text-ds-text">{t('preview.title')}</h2>
			<div className="flex items-end gap-2">
				<SelectInput
					label={t('preview.language')}
					value={props.locale}
					options={props.locales.map((l) => ({ id: l, label: l }))}
					onChange={props.onLocaleChange}
				/>
				<Button isDisabled={props.isBusy || !props.locale} onPress={props.onPreview}>
					{t('preview.run')}
				</Button>
			</div>
			{props.missing !== null ? (
				<div role="alert" className="text-ds-sm text-ds-danger">
					<p>{t('preview.missing')}</p>
					<p>{props.missing}</p>
				</div>
			) : null}
			{props.error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{props.error}
				</p>
			) : null}
			{props.result ? (
				<>
					<Block t={t} title={t('preview.withProduct')} items={props.result.withProduct} />
					<Block t={t} title={t('preview.pageEnd')} items={props.result.pageEnd} />
				</>
			) : null}
		</section>
	)
}
